import {
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  createComponent,
  EventEmitter,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from "@angular/core";
import { Subscription } from "rxjs";
import { Loader } from "@googlemaps/js-api-loader";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { RecordDtoWithPolyogns } from "src/app/models/recordDto.model";
import { ThemePalette } from "@angular/material/core";
import { ProgressSpinnerMode } from "@angular/material/progress-spinner";
import { HeatMap } from "src/app/models/heatmap.model";
import { CommonService } from "src/app/services/common.service";
import { MapMarkerComponent } from "src/app/common-components/map-marker/map-marker.component";
import { MappingGroupService } from "src/app/services/mapping-group.service";
import { MappingHistoryService } from "src/app/services/mapping-history.service";
import { OrganizationLevel } from "src/app/models/organizationLevel.model";
import { ParameterLookup } from "src/app/models/parameter.model";
import { DateFormatService } from "src/app/services/dateFormat.service";
import { BaseRealtimeMap } from "src/app/shared/maps/base-realtime-map";
import { ZoneData } from "src/app/models/mappingGroup.model";
import { LiveOption } from "src/app/models/liveOption.model";
import { ExtendedMapOptionsComponent } from "../extended-map-options/extended-map-options.component";
import { AirNowService } from "src/app/services/airNow.service";
import { MonitoringSite } from "src/app/models/monitoringSite.model";
import { InfoWindowComponent } from "src/app/components/info-window/info-window.component";
import { CustomMapBuildingService } from "src/app/services/custom-map-building.service";
import {
    CustomMapBuilding,
    Floor,
} from "src/app/models/customMapBuilding.model";
import { TranslateService } from "@ngx-translate/core";
import { getGoogleMapsLoader } from "src/app/_helpers/google-maps-loader-helper";

import { trackByIndex } from "src/app/shared/utils/track-by.util";
declare var google: any;

@Component({
  standalone: false,
    selector: "app-google-maps",
    templateUrl: "./google-maps.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./google-maps.component.scss"],
})
export class GoogleMapsComponent
    extends BaseRealtimeMap
    implements OnInit, OnDestroy
{
    scriptLoader!: Loader;
    gMap!: google.maps.Map;
    heatmap!: google.maps.visualization.HeatmapLayer;
    options!: google.maps.MapOptions;
    markers: google.maps.marker.AdvancedMarkerElement[] = [];
    airNowMarkers!: google.maps.marker.AdvancedMarkerElement[];
    streetViewService!: google.maps.StreetViewService;
    infoWindow!: google.maps.InfoWindow;
    bounds!: google.maps.LatLngBounds;
    markerClusterer: MarkerClusterer;
    airNowMarkerClusterer: MarkerClusterer;
    heatmapOverlay: any;
    mapMarker: ComponentRef<MapMarkerComponent>;
    extendedOptions: ComponentRef<ExtendedMapOptionsComponent>;
    subscriptions: Subscription[] = [];
    @Input() showHeatmap = false;
    @Input() spinnerColor?: ThemePalette;
    @Input() spinnerDiameter = 100;
    @Input() spinnerMode?: ProgressSpinnerMode = "indeterminate";
    @Input() spinnerStrokeWidth?: number;
    @Input() spinnerValue?: number;

    @Input() nodes: RecordDtoWithPolyogns[] = [];
    @Input() heatmapData: HeatMap[];
    @Input() heatmapImage: string;
    @Input() loading = false;
    @Input() zoneLatLng: ZoneData[];
    @Input() showZones = false;
    @Input() selectedNode: RecordDtoWithPolyogns;
    @Output() openNodeDetails = new EventEmitter<number>();
    @Output() toggleHeatmapEmit = new EventEmitter<boolean>();
    @Output() latLongRes = new EventEmitter<{
        value: string;
        isNew: boolean;
        updateIndex?: number;
    }>();
    @Output() polygonHover = new EventEmitter<{
        index: number;
        show: boolean;
    }>();

    customZoneList: google.maps.Polygon[] = [];
    infoWindowList: google.maps.InfoWindow[] = [];
    currentOverlay: google.maps.Marker | google.maps.Polygon | google.maps.Polyline | google.maps.Rectangle | google.maps.Circle;
    currEditableIndex = -1;
    currInfoWindowIndex = -1;
    windDirections: {
        polygon: google.maps.Polygon;
        marker: google.maps.Marker | null;
    }[] = [];
    liveOption: LiveOption;
    currentDisplayedTime: string;
    airNowSensorsEvent = new EventEmitter<boolean>();
    monitoringSites: MonitoringSite[] = [];
    currentInfoWindow = null;
    compRef: ComponentRef<InfoWindowComponent>;
    showFloors: boolean;
    selectedFloor: CustomMapBuilding;
    selectedFloorLevel: number | null = null;
    initialFloorLoaded: boolean;
    indoorKmlLayers: {
        id: string;
        floor: number;
        layer: google.maps.KmlLayer;
    }[] = [];
    private indoorSections: CustomMapBuilding[] = [];
    private indoorSectionsSubscription?: Subscription;
    private prevKmlLayer: google.maps.KmlLayer | null = null;
    floorLevels: Floor[] = [];
    nodesToDisplay: string[] = [];
    escapeKeyHandler: (e: KeyboardEvent) => void;
    isDestroyed = false;
    markerComponentRefs: ComponentRef<MapMarkerComponent>[] = [];

    constructor(
        private commonService: CommonService,
        private injector: Injector,
        private appRef: ApplicationRef,
        public mappingGroupService: MappingGroupService,
        public mappingHistoryService: MappingHistoryService,
        public customMapBuildingService: CustomMapBuildingService,
        public dateFormatService: DateFormatService,
        private airNowService: AirNowService,
        private changeDetectorRef: ChangeDetectorRef,
        private translateService: TranslateService,
    ) {
        super(mappingGroupService, mappingHistoryService, dateFormatService);
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.isDestroyed = true;
        this.indoorSectionsSubscription?.unsubscribe();
        this.subscriptions.forEach((s) => {
            s.unsubscribe();
        });
        if (this.gMap) {
            google.maps.event.clearListeners(this.gMap, "zoom_changed");
        }
        (this.markers ?? []).forEach((m) => {
            this.clearListeners(m);
        });
        (this.airNowMarkers ?? []).forEach((m) => {
            this.clearListeners(m);
        });
        (this.customZoneList ?? []).forEach((polygon) => {
            if (!polygon) return;
            this.clearListeners(polygon);
            this.clearListeners(polygon.getPath?.());
        });
        (this.infoWindowList ?? []).forEach((infoWindow) => {
            if (!infoWindow) return;
            this.clearListeners(infoWindow);
            infoWindow.close();
        });
        (this.windDirections ?? []).forEach(({ polygon, marker }) => {
            this.clearAndRemoveFromMap(polygon);
            this.clearAndRemoveFromMap(marker);
        });
        (this.indoorKmlLayers ?? []).forEach(({ layer }) => {
            this.clearAndRemoveFromMap(layer);
        });
        this.clearAndRemoveFromMap(this.currentOverlay);
        this.markerClusterer?.clearMarkers();
        this.airNowMarkerClusterer?.clearMarkers();
        this.heatmapOverlay?.setMap(null);
        this.compRef?.destroy();
        this.destroyComponent(this.extendedOptions);
        this.destroyComponent(this.mapMarker);
        if (this.escapeKeyHandler) {
            document.removeEventListener("keyup", this.escapeKeyHandler);
        }
        this.markerComponentRefs.forEach((ref) => {
            this.destroyComponent(ref);
        });
    }

    ngOnInit(): void {
        this.loadHeaderData();
        this.subscriptions.push(
            this.commonService.nodesUpdated.subscribe((centerMap: boolean) => {
                this.updateMap(centerMap);
            }),
        );

        const loader = getGoogleMapsLoader(this.translateService.currentLang);
        this.scriptLoader = loader;

        this.subscriptions.push(
            this.markerEvent.subscribe((res: boolean) => {
                this.setMarkers(res);
            }),
        );

        this.subscriptions.push(
            this.windDirEvent.subscribe((res: boolean) => {
                this.createWindCones();
            }),
        );

        this.subscriptions.push(
            this.commonService.liveOptionSubject.subscribe(
                (res) => (this.liveOption = res),
            ),
        );
    }

    clearListeners(
        target?:
            | google.maps.MVCObject
            | google.maps.marker.AdvancedMarkerElement
            | null,
    ) {
        if (target) {
            google.maps.event.clearInstanceListeners(target);
        }
    }

    clearAndRemoveFromMap(
        target?: google.maps.MVCObject & { setMap?: Function },
    ) {
        if (!target) return;
        google.maps.event.clearInstanceListeners(target);
        target.setMap?.(null);
    }

    destroyComponent(ref?: ComponentRef<any>) {
        if (!ref) return;
        this.appRef.detachView(ref.hostView);
        ref.destroy();
    }

    showAirNowSensors(isShow = true) {
        if (isShow && (!this.monitoringSites || !this.monitoringSites.length)) {
            this.airNowService.getMonitoringSites().subscribe((res) => {
                this.monitoringSites = res;
                this.toggleAirNowSensorsOnMap();
            });
        } else if (isShow) {
            this.toggleAirNowSensorsOnMap();
        } else {
            if (this.airNowMarkerClusterer) {
                this.airNowMarkerClusterer.clearMarkers();
            }
        }
    }

    toggleAirNowSensorsOnMap() {
        this.airNowMarkers = [];
        this.monitoringSites.forEach((site) => {
            const marker = new google.maps.Marker({
                position: {
                    lat: site.latitude,
                    lng: site.longitude,
                },
                icon: "../assets/images/green_triangle.png",
            });
            const infoWindow = new google.maps.InfoWindow();

            marker.addListener("click", (e) => {
                this.onMarkerClick(marker, site, infoWindow, e);
            });
            infoWindow.addListener("closeclick", (_) => this.compRef.destroy());

            this.airNowMarkers.push(marker);
            this.markerClusterRender(this.airNowMarkers, true);
        });
    }

    onMarkerClick(marker, site: MonitoringSite, infoWindow, e) {
        if (
            this.currentInfoWindow !== null &&
            this.currentInfoWindow !== undefined
        ) {
            this.currentInfoWindow.close();
        }
        if (this.compRef) {
            this.compRef.destroy();
        }

        this.compRef = createComponent(InfoWindowComponent, {
            environmentInjector: this.appRef.injector,
            elementInjector: this.injector,
        });

        this.compRef.instance.siteId = site.id;
        this.compRef.instance.latitude = site.latitude;
        this.compRef.instance.longitude = site.longitude;

        this.appRef.attachView(this.compRef.hostView);

        const div = document.createElement("div");
        div.appendChild(this.compRef.location.nativeElement);

        infoWindow.setContent(div);
        infoWindow.open(this.gMap, marker);
        this.currentInfoWindow = infoWindow;
    }

    async updateMap(centerMap: boolean) {
        try {
            this.currentDisplayedTime =
                this.dateFormatService.formatDateTimeWithTimeZoneToString(
                    new Date(),
                );
            if (!this.gMap) {
                google = await this.scriptLoader.load();
                this.options = {
                    center: { lat: 0, lng: 0 },
                    zoom: 14,
                    zoomControl: false,
                    scaleControl: true,
                    mapId: "4504f8b37365c3d0",
                    mapTypeControl: true,
                    mapTypeId: "satellite",
                    mapTypeControlOptions: {
                        style: google.maps.MapTypeControlStyle.DROPDOWN_MENU,
                        mapTypeIds: [
                            "roadmap",
                            "hybrid",
                            "terrain",
                            "satellite",
                        ],
                        position: google.maps.ControlPosition.LEFT_TOP,
                    },
                    streetViewControl: false,
                    rotateControl: true,
                    fullscreenControl: true,
                    gestureHandling: "greedy",
                };
                try {
                    const { Map: GoogleMap } = (await google.maps.importLibrary(
                        "maps",
                    )) as google.maps.MapsLibrary;

                    this.bounds = new google.maps.LatLngBounds();
                    this.nodes.forEach((location) => {
                        this.bounds.extend(
                            new google.maps.LatLng({
                                lat: location.lat as number,
                                lng: location.lon as number,
                            }),
                        );
                    });
                    this.options.center = this.bounds.getCenter().toJSON();

                    this.gMap = new GoogleMap(
                        document.getElementById("map") as HTMLElement,
                        this.options,
                    );
                    this.gMap.fitBounds(this.bounds);
                } catch (error) {
                    console.error(error, "error setting bounds");
                    return;
                }

                if (!this.showZones) {
                    this.mappingGroupContainer =
                        this.getMappingGroupSelectBox();
                    this.mappingGroupContainer.style.display = "none";
                    this.gMap.controls[
                        google.maps.ControlPosition.TOP_RIGHT
                    ].push(this.mappingGroupContainer);

                    this.extendedOptions = createComponent(ExtendedMapOptionsComponent, {
                        environmentInjector: this.appRef.injector,
                        elementInjector: this.injector,
                    });
                    this.appRef.attachView(this.extendedOptions.hostView);
                    this.gMap.controls[
                        google.maps.ControlPosition.BOTTOM_LEFT
                    ].push(
                        this.extendedOptions.location.nativeElement.children[0],
                    );
                    const optionsRef = this.extendedOptions.instance;
                    this.subscriptions.push(
                        optionsRef.showNodesEvent.subscribe((res) => {
                            this.showNodes = res;
                            this.markerEvent.next(false);
                        }),
                    );
                    this.subscriptions.push(
                        optionsRef.showWindDirEvent.subscribe((res) => {
                            this.showWindDir = res;
                            this.windDirEvent.next(res);
                        }),
                    );
                    this.subscriptions.push(
                        optionsRef.zoneRiskType.subscribe((res) => {
                            this.mappingGroupContainer.style.display = "block";
                            this.getZoneRiskData();
                            this.activePlayOption = res;
                            if (this.dustLayer.length > 0) {
                                this.showKmlFile(
                                    this.currentKmlIndex != null
                                        ? this.currentKmlIndex
                                        : this.dustLayer.length - 1,
                                );
                            }
                        }),
                    );
                    this.subscriptions.push(
                        optionsRef.showAirNowSensorsEvent.subscribe((res) => {
                            this.showAirNowSensors(res);
                        }),
                    );
                }
                if (!this.initialFloorLoaded && !this.showZones) {
                    this.initialFloorLoaded = true;
                    this.getIndoorSections();
                }
                try {
                    await this.setMarkers(false);
                    await this.createWindCones();
                    if (this.showHeatmap) {
                        await this.setHeatmap();
                    } else {
                        if (this.heatmapOverlay) {
                            this.heatmapOverlay.setMap(null);
                        }
                    }
                } catch (error) {
                    console.error(error, "setMarkers error");
                }
                if (this.showZones) this.createDrawingsOptions();
                this.gMap.addListener("zoom_changed", () => {
                    this.updateLabelStyles();
                });
            } else {
                await this.setMarkers(centerMap);
                await this.createWindCones();
                if (this.showHeatmap) {
                    await this.setHeatmap();
                } else {
                    if (this.heatmapOverlay) {
                        this.heatmapOverlay.setMap(null);
                    }
                }
            }
        } catch (error) {
            console.error(error, "error updating google map");
        }
    }

    private updateLabelStyles() {
        const zoomLevel = this.gMap.getZoom();
        const thresholdDistance = 1.5; /* distance in km */
        let isNearbyLabel = false;

        this.markers?.forEach((marker) => {
            const markerPosition = marker.position as google.maps.LatLngLiteral;
            isNearbyLabel = false;

            this.markers.forEach((otherMarker) => {
                if (marker !== otherMarker && !isNearbyLabel) {
                    const marker2Position =
                        otherMarker.position as google.maps.LatLngLiteral;
                    const distance = this.calculateDistance(
                        markerPosition,
                        marker2Position,
                    );
                    if (distance < thresholdDistance) {
                        isNearbyLabel = true;
                    }
                }
            });

            const nodeNameElement = marker.content as HTMLElement;
            const labelElement = nodeNameElement.querySelector(
                ".node-name",
            ) as HTMLElement;
            if (zoomLevel >= 14 || !isNearbyLabel) {
                labelElement.classList.add("show-full-label");
            } else {
                labelElement.classList.remove("show-full-label");
            }
        });
    }

    calculateDistance(
        origin: google.maps.LatLngLiteral,
        destination: google.maps.LatLngLiteral,
    ): number {
        const EARTH_RADIUS_KM = 6371; // Radius of the Earth in kilometers
        const degreesToRadians = (degrees: number): number =>
            degrees * (Math.PI / 180);

        const deltaLatitude = degreesToRadians(destination.lat - origin.lat);
        const deltaLongitude = degreesToRadians(destination.lng - origin.lng);

        const a =
            Math.sin(deltaLatitude / 2) * Math.sin(deltaLatitude / 2) +
            Math.cos(degreesToRadians(origin.lat)) *
                Math.cos(degreesToRadians(destination.lat)) *
                Math.sin(deltaLongitude / 2) *
                Math.sin(deltaLongitude / 2);

        const angularDistance = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return EARTH_RADIUS_KM * angularDistance; // Distance in kilometers
    }

    async createDrawingsOptions() {
        try {
            const { DrawingManager } =
                await google.maps.importLibrary("drawing");
            const drawingManager = new DrawingManager({
                drawingMode: null,
                drawingControl: true,
                drawingControlOptions: {
                    position: google.maps.ControlPosition.TOP_RIGHT,
                    drawingModes: [google.maps.drawing.OverlayType.POLYGON],
                },
                polygonOptions: {
                    fillColor: this.commonService.colors.white,
                    strokeColor: this.commonService.colors.white,
                    fillOpacity: 0.5,
                    strokeWeight: 5,
                    clickable: true,
                    zIndex: 1,
                },
            });

            google.maps.event.addListener(
                drawingManager,
                "overlaycomplete",
                (event) => {
                    this.currentOverlay = event.overlay;
                    if (
                        event.type ===
                            google.maps.drawing.OverlayType.POLYGON &&
                        drawingManager.getDrawingMode()
                    ) {
                        this.latLongRes.emit({
                            value: JSON.stringify(
                                event.overlay.getPath().getArray(),
                            ),
                            isNew: true,
                        });
                    } else {
                        this.currentOverlay.setMap(null);
                    }
                },
            );

            if (this.escapeKeyHandler) {
                document.removeEventListener("keyup", this.escapeKeyHandler);
            }
            this.escapeKeyHandler = (e: KeyboardEvent) => {
                if (e.key === "Escape" && drawingManager.getDrawingMode()) {
                    drawingManager.setDrawingMode(null);
                }
            };
            document.addEventListener("keyup", this.escapeKeyHandler);

            drawingManager.setMap(this.gMap);
        } catch (error) {
            console.error(error, "error setting drawing options");
        }
    }

    displayZone(zoneCords: ZoneData[], index: number, zoneName: string) {
        this.zoneLatLng = zoneCords;
        const polygonCoords = this.zoneLatLng;

        const customZone = (this.customZoneList[index] =
            new google.maps.Polygon({
                paths: polygonCoords,
                fillColor: this.commonService.colors.red,
                strokeColor: this.commonService.colors.red,
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillOpacity: 0.35,
                clickable: true,
                zIndex: 1,
            }));
        const infowindow = (this.infoWindowList[index] =
            new google.maps.InfoWindow());
        const centerBound = new google.maps.LatLngBounds();
        polygonCoords.forEach((location) => {
            centerBound.extend(
                new google.maps.LatLng({
                    lat: location.lat,
                    lng: location.lng,
                }),
            );
        });
        this.infoWindowList[index].setContent(zoneName);
        this.infoWindowList[index].setPosition(
            centerBound.getCenter().toJSON(),
        );

        google.maps.event.addListener(customZone, "click", (event) => {
            this.showInfoWindow(index, true);
            if (
                this.currEditableIndex != -1 &&
                this.currEditableIndex != index
            ) {
                this.customZoneList[this.currEditableIndex].setOptions({
                    draggable: false,
                    editable: false,
                    geodesic: false,
                    zIndex: 1,
                });
            }
            this.currEditableIndex = index;
            this.customZoneList[this.currEditableIndex].setOptions({
                draggable: true,
                editable: true,
                geodesic: true,
                zIndex: 2,
            });
        });

        google.maps.event.addListener(customZone, "mouseover", (event) => {
            this.focusOnPolygon(index, true);
        });

        google.maps.event.addListener(customZone, "mouseout", (event) => {
            this.focusOnPolygon(index, false);
        });

        google.maps.event.addListener(customZone.getPath(), "set_at", () => {
            infowindow.setMap(null);
            this.latLongRes.emit({
                value: JSON.stringify(customZone.getPath().getArray()),
                isNew: false,
                updateIndex: index,
            });
        });

        google.maps.event.addListener(customZone.getPath(), "insert_at", () => {
            infowindow.setMap(null);
            this.latLongRes.emit({
                value: JSON.stringify(customZone.getPath().getArray()),
                isNew: false,
                updateIndex: index,
            });
        });

        /* focus on polygon */
        if (index === 0) {
            this.bounds = new google.maps.LatLngBounds();
        }
        polygonCoords.forEach((location) => {
            this.bounds.extend(
                new google.maps.LatLng({
                    lat: location.lat,
                    lng: location.lng,
                }),
            );
        });
        if (index === this.customZoneList.length - 1) {
            this.gMap.setCenter(this.bounds.getCenter().toJSON());
            this.gMap.fitBounds(this.bounds);
        }

        this.customZoneList[index].setMap(this.gMap);
    }

    showInfoWindow(index, show) {
        if (show) {
            if (this.currInfoWindowIndex != -1) {
                this.infoWindowList[this.currInfoWindowIndex].close();
                this.focusOnPolygon(this.currInfoWindowIndex, false, true);
            }
            this.focusOnPolygon(index, true);
            this.currInfoWindowIndex = index;
            this.infoWindowList[index].open(this.gMap);
        } else {
            this.currInfoWindowIndex = -1;
            this.focusOnPolygon(index, false);
            this.infoWindowList[index].close();
        }
    }

    focusOnPolygon(index: number, show: boolean, isForceClose = false) {
        if (this.currInfoWindowIndex == index && !show && !isForceClose) {
            show = true;
        }
        if (this.customZoneList[index]) {
            this.customZoneList[index].setOptions({
                strokeColor: show
                    ? this.commonService.colors.greyLight
                    : this.commonService.colors.red,
                // To display polgon darker, if hovered.
                fillOpacity: show ? 0.5 : 0.35,
                // To display focused polygon on top, if two polygon areas are intersecting.
                zIndex: show ? 2 : 1,
            });
            this.polygonHover.emit({ index: index, show: show });
        }
    }

    updateCustomList(length: number) {
        if (this.customZoneList.length > 0) {
            this.customZoneList.forEach((data, i) => this.resetZone(i));
        }
        this.customZoneList = [];
        this.infoWindowList = [];
        for (let index = 0; index < length; index++) {
            this.customZoneList.push(null);
            this.infoWindowList.push(null);
        }
    }

    resetZone(index) {
        if (this.customZoneList[index]) this.customZoneList[index].setMap(null);
        if (this.infoWindowList[index]) this.infoWindowList[index].close();
    }

    resetCreatedZone() {
        if (this.currentOverlay) this.currentOverlay.setMap(null);
    }

    toggleHeatmap() {
        this.toggleHeatmapEmit.emit(true);
    }

    setHeatmap() {
        if (this.heatmapOverlay) {
            this.heatmapOverlay.setMap(null);
        }

        if (this.heatmapImage) {
            const bounds = new google.maps.LatLngBounds();
            this.heatmapData.forEach((latLongs) => {
                bounds.extend(
                    new google.maps.LatLng({
                        lat: parseFloat(latLongs.lat.toString()) as number,
                        lng: parseFloat(latLongs.lng.toString()) as number,
                    }),
                );
            });

            const srcImage = "data:image/jpeg;base64," + this.heatmapImage;

            class USGSOverlay extends google.maps.OverlayView {
                private bounds_: google.maps.LatLngBounds;
                private image_: string;
                private div_: HTMLElement | null;

                constructor(
                    boundsData: google.maps.LatLngBounds,
                    image: string,
                ) {
                    super();

                    this.bounds_ = boundsData;
                    this.image_ = image;
                    this.div_ = null;
                }

                onAdd() {
                    this.div_ = document.createElement("div");
                    this.div_.style.borderStyle = "none";
                    this.div_.style.borderWidth = "0px";
                    this.div_.style.position = "absolute";

                    const img = document.createElement("img");

                    img.src = this.image_;
                    img.style.width = "100%";
                    img.style.height = "100%";
                    img.style.position = "absolute";
                    img.style.opacity = "40%";
                    this.div_.appendChild(img);

                    const panes = this.getPanes()!;
                    panes.overlayLayer.appendChild(this.div_);
                }

                draw() {
                    const overlayProjection = this.getProjection();
                    const sw = overlayProjection.fromLatLngToDivPixel(
                        this.bounds_.getSouthWest(),
                    )!;
                    const ne = overlayProjection.fromLatLngToDivPixel(
                        this.bounds_.getNorthEast(),
                    )!;

                    if (this.div_) {
                        this.div_.style.left = sw.x + "px";
                        this.div_.style.top = ne.y + "px";
                        this.div_.style.width = ne.x - sw.x + "px";
                        this.div_.style.height = sw.y - ne.y + "px";
                    }
                }

                onRemove() {
                    if (this.div_) {
                        (this.div_.parentNode as HTMLElement).removeChild(
                            this.div_,
                        );
                        this.div_ = null;
                    }
                }
            }

            this.heatmapOverlay = new USGSOverlay(bounds, srcImage);
            this.heatmapOverlay.setMap(this.gMap);
        }

        // if (this.heatmapData.length > 0) {
        //   let heatmapData = this.heatmapData.map(latLongs => {
        //     if (latLongs?.weight) return { location: new google.maps.LatLng(latLongs.lat, latLongs.lng), weight: latLongs.weight };
        //     else return new google.maps.LatLng(latLongs.lat, latLongs.lng);
        //   });

        //   this.heatmap = new google.maps.visualization.HeatmapLayer({
        //     data: heatmapData
        //   });
        //   this.heatmap.setMap(this.gMap);
        // }
    }

    async setMarkers(centerMap: boolean) {
        try {
            const { AdvancedMarkerElement } = (await google.maps.importLibrary(
                "marker",
            )) as google.maps.MarkerLibrary;

            this.markerComponentRefs.forEach((ref) => {
                this.appRef.detachView(ref.hostView);
                ref.destroy();
            });
            this.markerComponentRefs = [];

            this.markers = (this.showNodes ? this.nodes : [])
                .map((node, i) => {
                    let parameterColor = this.commonService.getColorCode(
                        this.commonService.colors.greyDark,
                    );
                    if (node?.parameterValue) {
                        parameterColor =
                            node?.statusId !== 0
                                ? node?.parameterValue?.color ||
                                  this.commonService.getColorCode(
                                      this.commonService.colors.limeGreen,
                                  )
                                : this.commonService.getColorCode(
                                      this.commonService.colors.greyLight,
                                  );
                    }

                    const mapMarkerRef = createComponent(MapMarkerComponent, {
                        environmentInjector: this.appRef.injector,
                        elementInjector: this.injector,
                    });

                    mapMarkerRef.instance.id = i;
                    mapMarkerRef.instance.endPointName = node.endPointName;
                    mapMarkerRef.instance.markerType = "location";
                    mapMarkerRef.instance.parameterColor = parameterColor;
                    mapMarkerRef.instance.parameterValue =
                        node?.parameterValue?.name && node?.statusId !== 0
                            ? Math.round(node?.parameterValue?.value).toString()
                            : node?.value?.toFixed(2) || "--";
                    mapMarkerRef.instance.parameterType =
                        this.selectedParameter?.parameterType ?? null;
                    mapMarkerRef.instance.windSpeed = node?.windSpeed;
                    mapMarkerRef.instance.windDirection = node?.windDir;
                    this.appRef.attachView(mapMarkerRef.hostView);
                    this.markerComponentRefs.push(mapMarkerRef);

                    const marker = new AdvancedMarkerElement({
                        position: { lat: node.lat, lng: node.lon },
                        map: null,
                        content:
                            mapMarkerRef.location.nativeElement.children[0],
                    });

                    marker.addListener("click", () => {
                        if (marker?.element && marker?.element?.children) {
                            const nodeIndex = parseInt(
                                marker.element.children[0].id,
                            );
                            this.selectedNode = this.nodes[nodeIndex];
                            setTimeout(() => {
                                this.openNodeDetails.emit(nodeIndex);
                                if (this.liveOption.enabled)
                                    this.selectNode(this.selectedNode);
                            }, 100);
                        }
                    });

                    return this.nodesToDisplay.includes(node.endPointId)
                        ? marker
                        : null;
                })
                .filter((marker) => marker);

            if (
                this.dustLayer.length > 0 &&
                (this.currentKmlIndex != null || this.isPlaying)
            ) {
                this.clearPreviousKmlLayer();
                const kmzLayer =
                    this.currentKmlIndex != null
                        ? new google.maps.KmlLayer({
                              url: this.dustLayer[this.currentKmlIndex][
                                  this.activePlayOption === "Dust Map"
                                      ? "mapResultKmlFileUrl"
                                      : "zonedResultKmlFileUrl"
                              ],
                          })
                        : new google.maps.KmlLayer(null);
                kmzLayer.setMap(this.gMap);
                this.prevKmlLayer = kmzLayer;
                if (this.currentKmlIndex == null) {
                    this.markerClusterRender(this.markers);
                    this.isPlaying = false;
                }
            }

            if (centerMap) {
                this.bounds = new google.maps.LatLngBounds();
                this.nodes.forEach((location) => {
                    this.bounds.extend(
                        new google.maps.LatLng({
                            lat: location.lat as number,
                            lng: location.lon as number,
                        }),
                    );
                });
                this.gMap.setCenter(this.bounds.getCenter().toJSON());
                this.gMap.fitBounds(this.bounds);
            }
            this.markerClusterRender(this.markers);
            this.updateLabelStyles();
        } catch (error) {
            console.error(error, "error setting markers");
            this.markerComponentRefs.forEach((ref) => {
                this.destroyComponent(ref);
            });
            this.markerComponentRefs = [];
            this.destroyComponent(this.mapMarker);
            this.mapMarker = null;
        }
    }

    markerClusterRender(markers, isAirNowMarkers = false) {
        const renderer = {
            render({ count, position }, stats, map) {
                const title = `Cluster of ${count} markers`,
                    zIndex = Number(google.maps.Marker.MAX_ZINDEX) + count;
                const marker = document.createElement("div");
                const gradiant = isAirNowMarkers
                    ? "radial-gradient(circle, rgba(112, 173, 71, 0.5) 50%, rgba(144, 238, 144, 0.5) 100%);"
                    : "radial-gradient(circle, rgba(92, 59, 168, 1) 50%, rgba(169, 132, 255, 1) 100%);";
                marker.innerHTML = `
          <div style="height: 30px; width: 30px; background: ${gradiant} border-radius: 50%; display: inline-block;">
          <span style="color: #fff; display: flex; justify-content: center; align-items: center; height: 30px;">${count}</span>
          </div>
        `;

                const clusterOptions = {
                    map,
                    position,
                    zIndex,
                    title,
                    content: marker,
                };
                return new google.maps.marker.AdvancedMarkerElement(
                    clusterOptions,
                );
            },
        };

        if (isAirNowMarkers) {
            if (this.airNowMarkerClusterer) {
                this.airNowMarkerClusterer.clearMarkers();
            }
            this.airNowMarkerClusterer = new MarkerClusterer({
                markers: this.airNowMarkers,
                map: this.gMap,
                renderer: renderer,
            });
        } else {
            if (this.markerClusterer) {
                this.markerClusterer.clearMarkers();
            }
            this.markerClusterer = new MarkerClusterer({
                markers: this.markers,
                map: this.gMap,
                renderer: renderer,
            });
        }
    }

    selectNode(node: RecordDtoWithPolyogns) {
        this.selectedNode = node;
        if (this.selectedNode) {
            const bounds = new google.maps.LatLngBounds();
            bounds.extend(
                new google.maps.LatLng({
                    lat: this.selectedNode.lat as number,
                    lng: this.selectedNode.lon as number,
                }),
            );
            this.gMap.setCenter(bounds.getCenter().toJSON());
        }
        this.createWindCones();
    }

    resetWindCones() {
        this.windDirections.forEach((windDir) => {
            windDir.polygon?.setMap(null);
            windDir.marker?.setMap(null);
        });
    }

    createWindCones() {
        this.resetWindCones();
        this.windDirections = [];
        if (!this.showWindDir) return;
        if (!this.liveOption?.enabled) {
            if (!this.selectedNode) return;
            const node = this.selectedNode;
            node.polygons?.forEach((polygon) => {
                const shownColor = polygon?.color
                    ? "#" + polygon?.color
                    : this.commonService.colors.limeGreen;
                const windOpacity = Math.min(
                    Math.max((polygon.windFrequency / 100) * 2, 0.15),
                    0.5,
                );
                this.setConesCords(node, shownColor, windOpacity, polygon);
            });
            return;
        }
        this.nodes.forEach((node) => {
            if (
                node &&
                node.statusId != 0 &&
                this.nodesToDisplay.includes(node.endPointId) &&
                node.windDir != null
            ) {
                const shownColor = node?.parameterValue?.color
                    ? "#" + node?.parameterValue?.color
                    : this.commonService.colors.limeGreen;
                const windOpacity = 0.3;
                this.setConesCords(node, shownColor, windOpacity);
            }
        });
    }

    async setConesCords(node, shownColor, windOpacity, polygon = null) {
        const windSpeed = polygon?.windSpeed ?? node.windSpeed ?? 0;
        const coneLength = this.calculateConeLength(windSpeed);
        const triangleCoords = await this.getTriangleCoords(
            node,
            coneLength,
            polygon,
        );
        const windCones = new google.maps.Polygon({
            paths: triangleCoords,
            strokeColor: shownColor,
            strokeOpacity: windOpacity,
            strokeWeight: 2,
            fillColor: shownColor,
            fillOpacity: windOpacity,
        });
        windCones.setMap(this.gMap);
        this.windDirections.push({ polygon: windCones, marker: null });
    }

    async getTriangleCoords(node, distance, polygon = null) {
        try {
            const { spherical } = await google.maps.importLibrary("geometry");
            const origin = new google.maps.LatLng(node.lat, node.lon);
            const coord1 = spherical.computeOffset(origin, 0, 0);
            const coord2 = spherical.computeOffset(
                origin,
                distance,
                (polygon ? polygon.windAngleSpread1 : node.windDir + 15) + 180,
            );
            const coord3 = spherical.computeOffset(
                origin,
                distance,
                (polygon ? polygon.windAngleSpread2 : node.windDir - 15) + 180,
            );
            return [coord1, coord2, coord3];
        } catch (error) {
            console.error(error, "error setting triangle cords");
            return [];
        }
    }

    changeFloorLevel(layer: Floor, area?: CustomMapBuilding) {
        area = area ?? this.selectedFloor;
        this.clearKmllayer(area.id);
        area.floors.forEach((floor) =>
            floor.nodeIds.forEach((id) => {
                const curr = this.nodesToDisplay.findIndex(
                    (nodeId) => nodeId == id,
                );
                if (curr != -1) this.nodesToDisplay.splice(curr, 1);
            }),
        );

        setTimeout(() => {
            if (!this.isDestroyed) {
                this.nodesToDisplay = [
                    ...this.nodesToDisplay,
                    ...layer.nodeIds,
                ];
                this.setMarkers(false);
                this.createWindCones();
            }
        }, 100);

        const floorLayer = new google.maps.KmlLayer({
            url: layer.kmlFileUrl,
            map: this.gMap,
            preserveViewport: true,
            suppressInfoWindows: true,
        });
        this.selectedFloorLevel = layer.floor;
        google.maps.event.addListener(floorLayer, "click", () => {
            this.showFloors = true;
            this.selectedFloor = area;
            this.floorLevels = area.floors;
            this.changeDetectorRef.detectChanges();
        });
        this.indoorKmlLayers.push({
            id: area.id,
            floor: layer.floor,
            layer: floorLayer,
        });
    }

    protected override clearPreviousKmlLayer(): void {
        if (this.prevKmlLayer) {
            this.prevKmlLayer.setMap(null);
            this.prevKmlLayer = null;
        }
    }

    clearKmllayer(areaId: string) {
        const prevFloorIndex = this.indoorKmlLayers.findIndex(
            (layer) => layer.id === areaId,
        );
        if (prevFloorIndex != -1) {
            this.indoorKmlLayers[prevFloorIndex].layer.setMap(null);
            this.indoorKmlLayers.splice(prevFloorIndex, 1);
        }
    }

    /**
     * Refresh the indoor floorplans for the selected org.
     *
     * This runs on every live-mode data refresh (every 60s), so it must not throw
     * away the floor the user is looking at. When the floorplans coming back are
     * the ones already rendered we leave the KML layers and the floor picker alone
     * and only recompute which nodes are visible. A rebuild happens only when the
     * floorplans themselves changed, and even then each building is restored to the
     * floor it was showing rather than being forced back to floor 0.
     */
    getIndoorSections() {
        if (!this.initialFloorLoaded) return;
        /* Seed from the floors already on screen rather than from every node, so the
           markers do not flash the hidden floors while the request is in flight. */
        this.applyIndoorNodeVisibility(this.indoorSections);
        /* A refresh supersedes any request still in flight, so the 60s live refresh
           cannot stack subscriptions or race two responses against each other. */
        this.indoorSectionsSubscription?.unsubscribe();
        this.indoorSectionsSubscription = this.customMapBuildingService
            .getAllMaps(this.selectedOrg.id)
            .subscribe((res) => {
                /* This subscription outlives the view, so a response arriving after
                   the user navigated away must not touch the destroyed component. */
                if (this.isDestroyed) return;
                const sections = res ?? [];

                if (this.isSameIndoorSections(sections)) {
                    this.indoorSections = sections;
                    this.applyIndoorNodeVisibility(sections);
                    setTimeout(() => {
                        if (!this.isDestroyed) {
                            this.setMarkers(false);
                            this.createWindCones();
                        }
                    }, 100);
                    return;
                }

                const previousFloors = new Map(
                    this.indoorKmlLayers.map(
                        (layer): [string, number] => [layer.id, layer.floor],
                    ),
                );
                const previousBuildingId = this.selectedFloor?.id;
                this.indoorSections = sections;
                this.clearAllKmlLayers();
                this.nodesToDisplay = this.nodes.map((node) => node.endPointId);

                sections.forEach((area) => {
                    const wanted = previousFloors.get(area.id) ?? 0;
                    const layer =
                        area.floors.find((floor) => floor.floor === wanted) ??
                        area.floors.find((floor) => floor.floor === 0);
                    if (layer) this.changeFloorLevel(layer, area);
                });

                this.restoreFloorPicker(sections, previousBuildingId);
            });
    }

    /**
     * True when the floorplans returned by the API are the ones already rendered,
     * so there is nothing to tear down and rebuild.
     */
    private isSameIndoorSections(sections: CustomMapBuilding[]): boolean {
        const describe = (buildings: CustomMapBuilding[]) =>
            JSON.stringify(
                (buildings ?? [])
                    .map((area) => ({
                        id: area.id,
                        floors: (area.floors ?? [])
                            .map((floor) => ({
                                floor: floor.floor,
                                kmlFileUrl: floor.kmlFileUrl,
                                nodeIds: [...(floor.nodeIds ?? [])].sort(),
                            }))
                            .sort((a, b) => a.floor - b.floor),
                    }))
                    .sort((a, b) => a.id.localeCompare(b.id)),
            );
        return describe(this.indoorSections) === describe(sections);
    }

    /**
     * Recompute nodesToDisplay from the floors that are currently rendered, so a
     * refresh picks up newly reported nodes without revealing nodes that sit on a
     * floor the user has hidden.
     */
    private applyIndoorNodeVisibility(sections: CustomMapBuilding[]) {
        const renderedFloor = new Map(
            this.indoorKmlLayers.map(
                (layer): [string, number] => [layer.id, layer.floor],
            ),
        );
        const indoorNodeIds = new Set<string>();
        const visibleNodeIds = new Set<string>();
        (sections ?? []).forEach((area) => {
            const shown = renderedFloor.get(area.id);
            (area.floors ?? []).forEach((floor) =>
                (floor.nodeIds ?? []).forEach((id) => {
                    indoorNodeIds.add(id);
                    if (floor.floor === shown) visibleNodeIds.add(id);
                }),
            );
        });
        this.nodesToDisplay = this.nodes
            .map((node) => node.endPointId)
            .filter((id) => !indoorNodeIds.has(id) || visibleNodeIds.has(id));
    }

    private clearAllKmlLayers() {
        // clearKmllayer() splices indoorKmlLayers, so iterate over a copy.
        [...this.indoorKmlLayers].forEach((layer) =>
            this.clearKmllayer(layer.id),
        );
    }

    /**
     * Point the floor picker back at the building the user had open. If that
     * building no longer exists the picker closes, as it did before the refresh.
     */
    private restoreFloorPicker(
        sections: CustomMapBuilding[],
        previousBuildingId?: string,
    ) {
        if (!previousBuildingId) return;
        const building = (sections ?? []).find(
            (area) => area.id === previousBuildingId,
        );
        if (!building) {
            this.showFloors = false;
            this.floorLevels = [];
        } else {
            this.selectedFloor = building;
            this.floorLevels = building.floors;
            this.selectedFloorLevel =
                this.indoorKmlLayers.find((layer) => layer.id === building.id)
                    ?.floor ?? this.selectedFloorLevel;
        }
        this.changeDetectorRef.detectChanges();
    }

    setSelectedOrg(selectedOrg: OrganizationLevel) {
        this.selectedOrg = selectedOrg;
        this.getUpdatedZoneData();
    }

    setSelectedParameter(selectedParameter: ParameterLookup) {
        this.selectedParameter = selectedParameter;
        this.getUpdatedZoneData();
    }

    setSelectedDateRange(selectedDateRange: any) {
        this.selectedDateRange = selectedDateRange;
        this.getUpdatedZoneData();
    }

    loadHeaderData() {
        this.subscriptions.push(
            this.commonService.selectParameterEmitter.subscribe((parameter) => {
                if (parameter) {
                    this.setSelectedParameter(parameter);
                }
            }),
        );
        this.subscriptions.push(
            this.commonService.selectedOrgEmitter.subscribe((selectedOrg) => {
                if (selectedOrg) {
                    this.setSelectedOrg(selectedOrg);
                }
            }),
        );
        this.subscriptions.push(
            this.commonService.selectedDateFilterEmitter.subscribe(
                (dateFilter) => {
                    if (dateFilter) {
                        this.setSelectedDateRange(dateFilter);
                    }
                },
            ),
        );
    }

    public trackByIndex = trackByIndex;
}
